import { Inject, Injectable, REQUEST, inject, DOCUMENT } from '@angular/core';

import {Meta, Title} from "@angular/platform-browser";
import {XploreItemDTO} from "../../shared/object/xplorer-be-parsed-classes";
import {TerritoryService} from "../geolocation/territory.service";
import {getSubsiteSeoContent, SupportedLanguage} from "../../shared/subsite-seo-content";
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MetaDataService {

  private readonly NOINDEX_PREFIXES = [
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/load-user',
    '/logout',
    '/favorites',
    '/profile',
    '/map',
    '/debug',
    '/404'
  ];

  private metaTagService: Meta = inject(Meta);
  private titleService: Title = inject(Title);
  private territoryService: TerritoryService = inject(TerritoryService);
  private document: Document = inject(DOCUMENT);
  private router: Router = inject(Router);
  private request = inject(REQUEST, { optional: true });


  public setDefaultMetaData(routeUrl: string = this.getCurrentRouteUrl()): void {
    const seoContent = this.getSeoContent();
    this.updateTitle(seoContent.content.title);
    this.updateDescription(seoContent.content.description);
    this.updateKeywords(seoContent.content.keywords.join(', '), true);
    this.updateOpenGraphDefaults(seoContent.territoryName, seoContent.territoryCode);
    this.updateRouteSignals(routeUrl);
  }

  public applyRouteMeta(routeUrl: string): void {
    const pathName = this.extractPathname(routeUrl);
    const seoContent = this.getSeoContent();
    const isItalian = this.getCurrentLanguage() === 'it';

    if (pathName === '/' || pathName === '') {
      this.updateTitle(seoContent.content.title);
      this.updateDescription(seoContent.content.description);
    } else if (pathName.startsWith('/search')) {
      this.updateTitle(
        isItalian
          ? `Ricerca in ${seoContent.territoryName} | Xplorer`
          : `Search in ${seoContent.territoryName} | Xplorer`
      );
      this.updateDescription(
        isItalian
          ? `Trova luoghi, eventi ed esperienze locali nel territorio ${seoContent.territoryName}.`
          : `Find places, events and local experiences in ${seoContent.territoryName}.`
      );
    } else if (pathName === '/explore') {
      this.updateTitle(
        isItalian
          ? `Esplora ${seoContent.territoryName} | Xplorer`
          : `Explore ${seoContent.territoryName} | Xplorer`
      );
      this.updateDescription(
        isItalian
          ? `Scopri contenuti aggiornati e suggerimenti locali per ${seoContent.territoryName}.`
          : `Discover updated local content and practical suggestions for ${seoContent.territoryName}.`
      );
    } else if (pathName.startsWith('/explore/')) {
      this.updateTitle(
        isItalian
          ? `Esperienze in ${seoContent.territoryName} | Xplorer`
          : `Experiences in ${seoContent.territoryName} | Xplorer`
      );
      this.updateDescription(
        isItalian
          ? `Contenuti territoriali e pagine locali dedicate a ${seoContent.territoryName}.`
          : `Territory-specific pages and local content for ${seoContent.territoryName}.`
      );
    } else if (pathName === '/404') {
      this.updateTitle(
        isItalian
          ? `Pagina non trovata | Xplorer`
          : `Page not found | Xplorer`
      );
      this.updateDescription(
        isItalian
          ? `La pagina richiesta non e disponibile. Torna all'esplorazione dei contenuti locali.`
          : `The requested page is not available. Return to explore local content.`
      );
    } else {
      this.updateTitle(`${seoContent.territoryName} | Xplorer`);
      this.updateDescription(seoContent.content.description);
    }

    this.updateKeywords(seoContent.content.keywords.join(', '), true);
    this.updateOpenGraphDefaults(seoContent.territoryName, seoContent.territoryCode);
    this.updateRouteSignals(routeUrl);
  }

  public updateMetaDataByXploreItem (xploreItem: XploreItemDTO, routeUrl?: string) {
    if (xploreItem) {
      const territoryName = xploreItem.territory?.name || this.getSeoContent().territoryName;
      const description = xploreItem.shortDescription || `Scopri ${xploreItem.name} nel territorio ${territoryName}.`;
      const itemKeywords = (xploreItem.tags || []).map(tag => tag.code).filter(Boolean).join(',');
      const keywords = itemKeywords ? `${itemKeywords},${xploreItem.name},${territoryName}` : `${xploreItem.name},${territoryName}`;

      this.updateTitle(`${xploreItem.name} | ${territoryName} | Xplorer`);
      this.updateDescription(description);
      this.updateKeywords(keywords);
      this.metaTagService.updateTag({ property: 'og:type', content: 'article' });
    } else {
      this.setDefaultMetaData();
    }

    this.updateRouteSignals(routeUrl || this.getCurrentRouteUrl());
  }

  public updateRouteSignals(routeUrl: string = this.getCurrentRouteUrl()): void {
    const canonicalUrl = this.buildCanonicalUrl(routeUrl);
    this.upsertCanonicalLink(canonicalUrl);
    this.updateHreflangLinks(canonicalUrl);
    this.metaTagService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.updateRobotsMeta(this.shouldIndexRoute(this.extractPathname(routeUrl)));
  }

  private updateTitle(title: string) {
    this.titleService.setTitle(title);
    this.updateOgTitle(title);
  }

  private updateOgTitle(title: string) {
    this.metaTagService.updateTag({ property: 'og:title', content: title });
  }

  private updateDescription(description: string) {
    this.metaTagService.updateTag({ name: 'description', content: description });
    this.updateOgDescription(description);
  }

  private updateOgDescription(description: string) {
    this.metaTagService.updateTag({ property: 'og:description', content: description });
  }

  private updateKeywords(keywords: string, override: boolean = false) {
    if (override) {
      this.metaTagService.updateTag({ name: 'keywords', content: keywords});
      this.updateOgKeywords(keywords);
    } else {
      const defaultKeywords = this.getSeoContent().content.keywords.join(', ');
      this.metaTagService.updateTag({ name: 'keywords', content: defaultKeywords + ',' + keywords});
      this.updateOgKeywords(defaultKeywords + ',' + keywords);
    }

  }

  private updateOgKeywords(keywords: string) {
    this.metaTagService.updateTag({ property: 'og:keywords', content: keywords });
  }

  private updateOpenGraphDefaults(territoryName: string, territoryCode: string): void {
    this.metaTagService.updateTag({ property: 'og:type', content: 'website' });
    this.metaTagService.updateTag({ property: 'og:site_name', content: `Xplorer ${territoryName}` });
    this.metaTagService.updateTag({
      property: 'og:image',
      content: `${this.getCurrentOrigin()}/assets/xplorer/${territoryCode}/bg_territory_home.webp`
    });
  }

  private updateRobotsMeta(shouldIndex: boolean): void {
    this.metaTagService.updateTag({
      name: 'robots',
      content: shouldIndex ? 'index,follow,max-image-preview:large' : 'noindex,nofollow'
    });
  }

  private shouldIndexRoute(pathName: string): boolean {
    return !this.NOINDEX_PREFIXES.some(prefix => pathName === prefix || pathName.startsWith(`${prefix}/`));
  }

  private buildCanonicalUrl(routeUrl: string): string {
    const parsedUrl = new URL(routeUrl || '/', this.getCurrentOrigin());
    parsedUrl.hash = '';

    const lang = parsedUrl.searchParams.get('lang');
    parsedUrl.search = '';
    if (lang && this.isSupportedLanguage(lang)) {
      parsedUrl.searchParams.set('lang', lang.toLowerCase());
    }

    return parsedUrl.toString();
  }

  private updateHreflangLinks(canonicalUrl: string): void {
    const canonical = new URL(canonicalUrl);
    const cleanPath = canonical.pathname;
    const origin = canonical.origin;

    this.upsertAlternateLink(`${origin}${cleanPath}?lang=it`, 'it');
    this.upsertAlternateLink(`${origin}${cleanPath}?lang=en`, 'en');
    this.upsertAlternateLink(`${origin}${cleanPath}`, 'x-default');
  }

  private upsertCanonicalLink(href: string): void {
    let linkElement = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkElement) {
      linkElement = this.document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      this.document.head.appendChild(linkElement);
    }

    linkElement.setAttribute('href', href);
  }

  private upsertAlternateLink(href: string, hreflang: string): void {
    let linkElement = this.document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement;
    if (!linkElement) {
      linkElement = this.document.createElement('link');
      linkElement.setAttribute('rel', 'alternate');
      linkElement.setAttribute('hreflang', hreflang);
      this.document.head.appendChild(linkElement);
    }

    linkElement.setAttribute('href', href);
  }

  private getCurrentRouteUrl(): string {
    if (typeof window === 'undefined') {
      if (this.request?.url) {
        return this.request.url;
      }
      return this.router.url || '/';
    }

    return `${window.location.pathname}${window.location.search}`;
  }

  private extractPathname(routeUrl: string): string {
    return new URL(routeUrl || '/', this.getCurrentOrigin()).pathname;
  }

  private getSeoContent() {
    const territoryCode = this.territoryService.getTerritoryCodeFromUrl();
    const language = this.getCurrentLanguage();
    return getSubsiteSeoContent(territoryCode, language);
  }

  private getCurrentLanguage(): SupportedLanguage {
    if (typeof window === 'undefined') {
      return 'en';
    }

    const language = localStorage.getItem('language') || window.navigator.language;
    if (language?.toLowerCase().startsWith('it')) {
      return 'it';
    }

    return 'en';
  }

  private isSupportedLanguage(language: string): boolean {
    const normalizedLanguage = language.toLowerCase();
    return normalizedLanguage === 'it' || normalizedLanguage === 'en';
  }

  private getCurrentOrigin(): string {
    if (typeof window === 'undefined') {
      return 'https://xplorer-platform.com';
    }

    return window.location.origin;
  }
}
